import { AFFIX_CAPS, AFFIX_OF_STAT, AFFIXES, clampIlv, rollAffixes, type AffixRoll, type RollOpts } from './affixes';
import { armourPlusStats, forgeStats, type WeaponForge } from './forge';
import { gemOf, gemStats, MAX_SOCKETS, RARE_SOCKET_CHANCE, START_SOCKETS } from './itemCraft';
import { ACCESSORIES, MORE_GEAR } from './gear';
import { BOSS_GEAR } from './bossGear';
import { ITEM_SETS, SET_OF_PIECE, setBonusesAt } from './itemSets';
import type { Rng } from '../rng';
import { DUNGEON_GEAR } from './dungeon';
import { TASSEL_ITEM } from './plainsFill';
import type { WeaponKind } from './weapons';
import { SIGNATURE_ITEMS } from './codex';

/**
 * Equipment: where it is worn, what it adds, and how it changes the wearer's look (docs/GDD.md "อุปกรณ์และของ").
 * Six worn slots (หัว, เสื้อ, กางเกง, รองเท้า, อาวุธ, อาวุธลับ) and two special ones: สมบัติ (a treasure, a passive)
 * and ตำราสกิล (a skill manual: more damage with one art's moves). `weapon`, `head` and `body` keep their old keys,
 * so saves from before the other slots load as they are; only those three have a look on the figure (`visual`), the
 * others give stats only (the character models are final).
 */
export type EquipSlot =
  | 'weapon'
  | 'head'
  | 'body'
  | 'legs'
  | 'feet'
  | 'hidden'
  | 'treasure'
  | 'manual'
  // Items v2 (docs/systems/ITEMS_V2.md §1.2): stat-only accessories; old saves have them empty.
  | 'ring'
  | 'pendant';
/** Every slot; the first three in the order the renderer's outfit keys come in (weapon, head, body). */
export const EQUIP_SLOTS: readonly EquipSlot[] = [
  'weapon',
  'head',
  'body',
  'legs',
  'feet',
  'hidden',
  'treasure',
  'manual',
  'ring',
  'pendant',
];
/** The worn slots, and the special ones, as the bag's doll lays them out. */
export const WORN_SLOTS: readonly EquipSlot[] = ['head', 'body', 'legs', 'feet', 'weapon', 'hidden'];
export const SPECIAL_SLOTS: readonly EquipSlot[] = ['treasure', 'manual', 'ring', 'pendant'];
export const SLOT_LABELS: Readonly<Record<EquipSlot, string>> = {
  weapon: 'อาวุธ',
  head: 'หัว',
  body: 'เสื้อ',
  legs: 'กางเกง',
  feet: 'รองเท้า',
  hidden: 'อาวุธลับ',
  treasure: 'สมบัติ',
  manual: 'ตำราวิชา',
  ring: 'แหวน',
  pendant: 'หยกห้อย',
};
export const isEquipSlot = (v: unknown): v is EquipSlot =>
  typeof v === 'string' && (EQUIP_SLOTS as readonly string[]).includes(v);

/** `fine` and `mythic` came with items v2 (docs/systems/ITEMS_V2.md §1.4); old ids keep their meaning. */
export type Rarity = 'common' | 'fine' | 'rare' | 'epic' | 'legend' | 'mythic';
/** Rarities from lowest to highest. */
export const RARITIES: readonly Rarity[] = ['common', 'fine', 'rare', 'epic', 'legend', 'mythic'];
export const RARITY_LABELS: Readonly<Record<Rarity, string>> = {
  common: 'ของตลาด',
  fine: 'งานช่าง',
  rare: 'ครูช่าง',
  epic: 'สูตรลับ',
  legend: 'ตกทอด',
  mythic: 'ฟ้าสร้าง',
};
export const isRarity = (v: unknown): v is Rarity => typeof v === 'string' && (RARITIES as readonly string[]).includes(v);

export interface ItemStats {
  atk?: number;
  def?: number;
  maxHp?: number;
  /** Added to crit chance (0–1). */
  crit?: number;
  /** Fraction of damage dealt healed back. */
  lifesteal?: number;
  /** Movement speed bonus (0.05 = 5% faster). */
  speed?: number;
  // ── Items v2 affixes (data/affixes.ts); fractions unless noted. ──
  /** Ratings added to the derived stats (stats.ts `DerivedStats`). */
  hit?: number;
  magic?: number;
  magicHit?: number;
  magicCrit?: number;
  magicDef?: number;
  dodge?: number;
  toughness?: number;
  maxQi?: number;
  maxStamina?: number;
  /** Per second. */
  qiRegen?: number;
  hpRegen?: number;
  /** Combat hooks (wired in R2: docs/systems/ITEMS_V2.md §5.2). */
  critDmg?: number;
  defPierce?: number;
  qiCostPct?: number;
  cdPct?: number;
  rageGain?: number;
  guardBreak?: number;
  guardHp?: number;
  breakWindup?: number;
  counterDmg?: number;
  poisonChance?: number;
  bleedChance?: number;
  stunPower?: number;
  poisonRes?: number;
  bleedRes?: number;
  critRes?: number;
  flyCostPct?: number;
  glideLen?: number;
  fallRes?: number;
  resFire?: number;
  resWater?: number;
  resEarth?: number;
  resWind?: number;
  resPoison?: number;
  /** Damage of blows struck in the air (set bonus). */
  airDmg?: number;
}

/** Every `ItemStats` key at 0 (the sum `equipmentStats` starts from). */
const ZERO_STATS: Required<ItemStats> = {
  atk: 0,
  def: 0,
  maxHp: 0,
  crit: 0,
  lifesteal: 0,
  speed: 0,
  hit: 0,
  magic: 0,
  magicHit: 0,
  magicCrit: 0,
  magicDef: 0,
  dodge: 0,
  toughness: 0,
  maxQi: 0,
  maxStamina: 0,
  qiRegen: 0,
  hpRegen: 0,
  critDmg: 0,
  defPierce: 0,
  qiCostPct: 0,
  cdPct: 0,
  rageGain: 0,
  guardBreak: 0,
  guardHp: 0,
  breakWindup: 0,
  counterDmg: 0,
  poisonChance: 0,
  bleedChance: 0,
  stunPower: 0,
  poisonRes: 0,
  bleedRes: 0,
  critRes: 0,
  flyCostPct: 0,
  glideLen: 0,
  fallRes: 0,
  resFire: 0,
  resWater: 0,
  resEarth: 0,
  resWind: 0,
  resPoison: 0,
  airDmg: 0,
};
export const ITEM_STAT_KEYS = Object.keys(ZERO_STATS) as readonly (keyof ItemStats)[];

/**
 * A dropped item with its own rolled stats (it lives in the progress under its own id). Items v2
 * (docs/systems/ITEMS_V2.md §5.1): `affixes` is the truth when present; `stats` and `art` stay, derived from the base and
 * the affixes, for code that reads them. A roll without `affixes` is a pre-v2 one (`migrateRoll` turns it into
 * affixes on load).
 */
export interface ItemRoll {
  /** The item it is an instance of. */
  readonly base: string;
  readonly stats: Readonly<ItemStats>;
  /** Extra damage dealt with one martial art (fraction). */
  readonly art?: { readonly id: string; readonly pct: number };
  /** A forged weapon's + and element (see data/forge); `stats` stay the unforged ones. */
  readonly forge?: WeaponForge;
  /** Item level 1–60. */
  readonly ilv?: number;
  /** Its own rarity (may be above the base's). */
  readonly rarity?: Rarity;
  readonly affixes?: readonly AffixRoll[];
  /** ล้างค่า count on this item (R3: the cost doubles per reroll). */
  readonly rerolls?: number;
  /** ฝังหยก: a gem good id (data/itemCraft.ts) per socket, or null for an empty one. */
  readonly sockets?: readonly (string | null)[];
}

export interface ItemDef {
  readonly id: string;
  readonly name: string;
  readonly slot: EquipSlot;
  readonly rarity: Rarity;
  readonly desc: string;
  readonly stats: Readonly<ItemStats>;
  /** A weapon's kind (data/weapons); weapons only. */
  readonly kind?: WeaponKind;
  /**
   * Appearance key the client maps to colours and parts (a blade tint, a hat, a coat); empty for a piece with no
   * look on the figure (trousers, shoes, hidden weapons, treasures, manuals: stats only).
   */
  readonly visual: string;
  /** A hidden weapon's throw: the move it looses (data/hiddenWeapons.ts `HIDDEN_SKILLS`), from the อาวุธลับ slot. */
  readonly throwSkill?: string;
  /** Rolled items: extra damage with one martial art. */
  readonly art?: { readonly id: string; readonly pct: number };
  /** Forged weapons: their + and element. */
  readonly forge?: WeaponForge;
  /** What it is worth to the merchant before the sell rate (crafted gear; see data/shop). */
  readonly worth?: number;
  /** Item level 1–60 (default by rarity, `baseIlv`). */
  readonly ilv?: number;
  /** A rolled piece's affixes (items v2). */
  readonly affixes?: readonly AffixRoll[];
  /** The set it belongs to (data/itemSets.ts). */
  readonly set?: string;
  /** Its unique effect (data/uniques.ts); one of each works per character. */
  readonly unique?: string;
}

const list: ItemDef[] = [
  {
    id: 'iron-sword',
    name: 'กระบี่เหล็กกล้า',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'common',
    desc: 'กระบี่ประจำกายของผู้เริ่มท่องยุทธภพ คมพอใช้',
    stats: { atk: 3 },
    visual: 'steel',
  },
  {
    id: 'azure-sword',
    name: 'กระบี่ครามวารี',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'rare',
    desc: 'ใบกระบี่สีครามดุจน้ำลึก ฟันเร็วจนแสงสะท้อนเป็นสาย',
    stats: { atk: 8, crit: 0.03 },
    visual: 'azure',
  },
  {
    id: 'crimson-sword',
    name: 'กระบี่โลหิตชาด',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'epic',
    desc: 'กระบี่ของปีศาจกระบี่ทมิฬ ใบสีชาดยังอุ่นด้วยไอสังหาร',
    stats: { atk: 14, crit: 0.05 },
    visual: 'crimson',
  },
  {
    id: 'red-band',
    name: 'ผ้าคาดศีรษะแดง',
    slot: 'head',
    rarity: 'common',
    desc: 'ผ้าคาดของโจรภูเขา ผูกแน่นกันเหงื่อเข้าตา',
    stats: { atk: 1, def: 1 },
    visual: 'band',
  },
  {
    id: 'bamboo-hat',
    name: 'งอบไม้ไผ่พเนจร',
    slot: 'head',
    rarity: 'rare',
    desc: 'งอบกว้างบังแดดบังฝน จอมยุทธ์พเนจรไม่ห่างกาย',
    stats: { def: 4, maxHp: 20 },
    visual: 'douli',
  },
  {
    id: 'jade-crown',
    name: 'มงกุฎหยกมรกต',
    slot: 'head',
    rarity: 'epic',
    desc: 'มงกุฎหยกรัดมวยผม ว่ากันว่าช่วยให้ลมปราณสงบ',
    stats: { maxHp: 40, crit: 0.02 },
    visual: 'jadeCrown',
  },
  {
    id: 'navy-coat',
    name: 'เสื้อคลุมครามปักทอง',
    slot: 'body',
    rarity: 'common',
    desc: 'เสื้อคลุมแขนกุดขอบปักดิ้นทอง ชุดเดินทางของจอมยุทธ์',
    stats: { def: 3 },
    visual: 'coatNavy',
  },
  {
    id: 'wolf-pelt',
    name: 'หนังหมาป่าเงาจันทร์',
    slot: 'body',
    rarity: 'rare',
    desc: 'หนังหมาป่าสีเงินพาดไหล่ อุ่นและเหนียวกว่าผ้าธรรมดา',
    stats: { def: 5, maxHp: 30 },
    visual: 'pelt',
  },
  {
    id: 'black-vest',
    name: 'เสื้อกั๊กดำนักพรต',
    slot: 'body',
    rarity: 'rare',
    desc: 'เสื้อกั๊กสีหมึก ตัดเย็บแบบนักพรตแห่งขุนเขา',
    stats: { def: 7 },
    visual: 'coatBlack',
  },
  {
    id: 'crimson-coat',
    name: 'เสื้อคลุมชาดทมิฬ',
    slot: 'body',
    rarity: 'epic',
    desc: 'เสื้อคลุมสีชาดขอบทองของปีศาจกระบี่ทมิฬ',
    stats: { def: 10, maxHp: 40 },
    visual: 'coatCrimson',
  },
  {
    id: 'founder-sabre',
    name: 'กระบี่มารพันกระบี่',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'legend',
    desc: 'กระบี่ของจอมมารพันกระบี่ ทุกเล่มไม่เหมือนกัน ปลุกพลังวิชากระบี่หนึ่งวิชาให้รุนแรงขึ้น',
    stats: { atk: 24 },
    visual: 'demonblade',
  },
  // Ladder gear, bought with ladder points.
  {
    id: 'heaven-sword',
    name: 'กระบี่บันไดสวรรค์',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'epic',
    desc: 'กระบี่ที่มอบแก่ผู้ไต่บันไดสวรรค์ ใบกระบี่ใสดุจฟ้าหลังฝน',
    stats: { atk: 12, crit: 0.03 },
    visual: 'azure',
  },
  {
    id: 'heaven-crown',
    name: 'มงกุฎหยกบันไดสวรรค์',
    slot: 'head',
    rarity: 'epic',
    desc: 'มงกุฎหยกของผู้ประลองบนบันไดสวรรค์ ปกป้องจิตให้นิ่งกลางศึก',
    stats: { def: 6, maxHp: 60 },
    visual: 'jadeCrown',
  },
  {
    id: 'hell-sabre',
    name: 'ดาบบันไดนรก',
    slot: 'weapon',
    kind: 'dao',
    rarity: 'epic',
    desc: 'ดาบที่ดื่มเลือดผู้แพ้บนบันไดนรก ทุกแผลที่ฟันคืนพลังให้ผู้ถือ',
    stats: { atk: 13, lifesteal: 0.03 },
    visual: 'crimson',
  },
  {
    id: 'hell-coat',
    name: 'เสื้อคลุมบันไดนรก',
    slot: 'body',
    rarity: 'epic',
    desc: 'เสื้อคลุมดำของผู้รอดจากบันไดนรก หนาพอรับคมดาบ',
    stats: { def: 12, maxHp: 50 },
    visual: 'coatBlack',
  },
  // Crafted by the trades (data/professions.ts recipes); they forge like any weapon.
  {
    id: 'smith-sword',
    name: 'กระบี่เหล็กตีมือ',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'common',
    desc: 'กระบี่ที่ตีขึ้นเองจากเหล็กแท่ง หนักมือกว่าของร้าน',
    stats: { atk: 5 },
    visual: 'steel',
    worth: 120,
  },
  {
    id: 'smith-blade',
    name: 'กระบี่เหล็กดำ',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'rare',
    desc: 'กระบี่เหล็กดำจากยอดเขาหิมะ ด้ามไม้โอ๊ก คมไม่บิ่น',
    stats: { atk: 10, crit: 0.03 },
    visual: 'steel',
    worth: 520,
  },
  {
    id: 'smith-helm',
    name: 'หมวกเหล็กบุผ้า',
    slot: 'head',
    rarity: 'common',
    desc: 'หมวกเหล็กบุผ้าฝ้าย กันคมดาบที่ฟันลงกลางศีรษะ',
    stats: { def: 3, maxHp: 15 },
    visual: 'band',
    worth: 110,
  },
  {
    id: 'smith-mail',
    name: 'เสื้อเกราะเกล็ดเหล็ก',
    slot: 'body',
    rarity: 'rare',
    desc: 'เกล็ดเหล็กร้อยบนเสื้อผ้าฝ้าย หนักแต่รับดาบได้ดี',
    stats: { def: 8, maxHp: 25 },
    visual: 'coatBlack',
    worth: 420,
  },
  {
    id: 'tailor-hat',
    name: 'งอบฟางสาน',
    slot: 'head',
    rarity: 'common',
    desc: 'งอบฟางสานมือจากนาของตัวเอง บังแดดบังฝน',
    stats: { def: 2, maxHp: 12 },
    visual: 'douli',
    worth: 80,
  },
  {
    id: 'tailor-robe',
    name: 'เสื้อคลุมฝ้ายย้อมคราม',
    slot: 'body',
    rarity: 'common',
    desc: 'เสื้อคลุมผ้าฝ้ายย้อมครามเข้ม เย็บตะเข็บคู่',
    stats: { def: 3, maxHp: 15 },
    visual: 'coatNavy',
    worth: 110,
  },
  {
    id: 'tailor-silk',
    name: 'เสื้อคลุมไหมราตรี',
    slot: 'body',
    rarity: 'rare',
    desc: 'เสื้อคลุมไหมย้อมครามจนเกือบดำ เบาจนแทบไม่รู้สึก',
    stats: { def: 6, maxHp: 30, speed: 0.03 },
    visual: 'coatBlack',
    worth: 460,
  },
  {
    id: 'tailor-turban',
    name: 'ผ้าโพกไหมหลินจือ',
    slot: 'head',
    rarity: 'rare',
    desc: 'ผ้าโพกไหมย้อมน้ำเห็ดหลินจือ ว่ากันว่าช่วยให้จิตนิ่ง',
    stats: { maxHp: 35, crit: 0.02 },
    visual: 'jadeCrown',
    worth: 480,
  },
  // Weapons of every family on the reference sheet (docs/art/weapons-ref), sized like the swords:
  // common 3–5 damage, rare 7–11 with one more stat, epic 12–14 with two.
  {
    id: 'iron-dao',
    name: 'ดาบเหล็กสันหนา',
    slot: 'weapon',
    kind: 'dao',
    rarity: 'common',
    desc: 'ดาบคมเดียวสันหนา ฟันฟืนก็ได้ ฟันโจรก็ดี',
    stats: { atk: 4 },
    visual: 'steel',
    worth: 90,
  },
  {
    id: 'great-dao',
    name: 'ดาบใหญ่พันผ้าชาด',
    slot: 'weapon',
    kind: 'dao',
    rarity: 'rare',
    desc: 'ดาบใบกว้างหนักเกือบเท่าคน ด้ามพันผ้าแดง ฟันลงครั้งเดียวการ์ดของศัตรูสั่นทั้งตัว',
    stats: { atk: 11, maxHp: 20 },
    visual: 'crimson',
    worth: 480,
  },
  {
    id: 'tassel-qiang',
    name: 'ทวนพู่แดง',
    slot: 'weapon',
    kind: 'qiang',
    rarity: 'common',
    desc: 'ทวนด้ามไม้ หัวเหล็กเรียว ใต้หัวผูกพู่ขนม้าแดงไว้ซับเลือดไม่ให้ไหลลงด้าม',
    stats: { atk: 4, def: 1 },
    visual: 'steel',
    worth: 100,
  },
  {
    id: 'crescent-glaive',
    name: 'ง้าวจันทร์เสี้ยว',
    slot: 'weapon',
    kind: 'glaive',
    rarity: 'rare',
    desc: 'ง้าวใบโค้งดุจจันทร์เสี้ยวบนด้ามยาว กวาดทีเดียวกันศัตรูไว้ได้ทั้งวง',
    stats: { atk: 10, def: 2 },
    visual: 'steel',
    worth: 500,
  },
  {
    id: 'twin-hooks',
    name: 'ขอเกี่ยวคู่เสี้ยวเงิน',
    slot: 'weapon',
    kind: 'hook',
    rarity: 'rare',
    desc: 'ใบมีดโค้งคู่ปลายงอเป็นขอ ผูกพู่ม่วง เกี่ยวอาวุธศัตรูแล้วกระชากออกจากมือ',
    stats: { atk: 8, crit: 0.04 },
    visual: 'steel',
    worth: 460,
  },
  {
    id: 'viper-kris',
    name: 'มีดสั้นเขี้ยวงู',
    slot: 'weapon',
    kind: 'kris',
    rarity: 'rare',
    desc: 'มีดสั้นใบคดดุจงูเลื้อย ซ่อนในแขนเสื้อได้ แทงเข้าช่องว่างของเกราะได้แม่นยำ',
    stats: { atk: 7, crit: 0.06 },
    visual: 'steel',
    worth: 440,
  },
  {
    id: 'iron-claw',
    name: 'กรงเล็บเหล็กดำ',
    slot: 'weapon',
    kind: 'fist',
    rarity: 'common',
    desc: 'ถุงมือหนังติดกรงเล็บเหล็กสี่ซี่ ข่วนทีเดียวเสื้อเกราะขาดเป็นริ้ว',
    stats: { atk: 4, crit: 0.02 },
    visual: 'steel',
    worth: 100,
  },
  {
    id: 'iron-fan',
    name: 'พัดเหล็กบุปผาชาด',
    slot: 'weapon',
    kind: 'fan',
    rarity: 'rare',
    desc: 'พัดซี่เหล็กหุ้มผ้าไหมวาดดอกเหมยแดง กางออกปัดลูกดอก หุบเข้าตีจุดลมปราณ',
    stats: { atk: 7, crit: 0.03, speed: 0.02 },
    visual: 'crimson',
    worth: 460,
  },
  {
    id: 'sword-umbrella',
    name: 'ร่มซ่อนกระบี่',
    slot: 'weapon',
    kind: 'umbrella',
    rarity: 'epic',
    desc: 'ร่มกระดาษลงน้ำมันสีชาด ในด้ามซ่อนกระบี่บางเฉียบ กางรับฝนลูกดอก แล้วชักกระบี่ออกจากด้ามในพริบตา',
    stats: { atk: 12, def: 3, crit: 0.02 },
    visual: 'crimson',
    worth: 640,
  },
  {
    id: 'horn-bow',
    name: 'ธนูเขาสัตว์ลงรัก',
    slot: 'weapon',
    kind: 'bow',
    rarity: 'rare',
    desc: 'ธนูประกบเขาสัตว์กับไม้ ลงรักดำขลิบทอง น้าวหนักแต่ยิงไกลกว่าธนูไม้ธรรมดาเท่าตัว',
    stats: { atk: 9, crit: 0.03 },
    visual: 'steel',
    worth: 480,
  },
  {
    id: 'repeating-crossbow',
    name: 'หน้าไม้กลไกสามดอก',
    slot: 'weapon',
    kind: 'crossbow',
    rarity: 'rare',
    desc: 'หน้าไม้ไม้ดำมีรางลูกดอกบนหลัง ง้างครั้งเดียวยิงได้สามดอกติดกัน ช่างกลแห่งทางโคมเหล็กประดิษฐ์ขึ้น',
    stats: { atk: 10 },
    visual: 'steel',
    worth: 500,
  },
  {
    id: 'bladed-rings',
    name: 'ห่วงใบมีดคู่',
    slot: 'weapon',
    kind: 'ring',
    rarity: 'rare',
    desc: 'ห่วงเหล็กขอบคมคู่หนึ่ง ผูกเชือกไว้กับข้อมือ ขว้างออกไปหมุนกลับเข้ามือดุจนกนางแอ่น',
    stats: { atk: 8, speed: 0.03 },
    visual: 'steel',
    worth: 460,
  },
  {
    id: 'oak-staff',
    name: 'กระบองไม้โอ๊ก',
    slot: 'weapon',
    kind: 'staff',
    rarity: 'common',
    desc: 'กระบองไม้โอ๊กยาวเท่าคน หัวท้ายหุ้มทองเหลือง ตีไม่ถึงตายแต่ล้มทุกครั้ง',
    stats: { atk: 3, def: 2 },
    visual: 'steel',
    worth: 80,
  },
  {
    id: 'meteor-hammer',
    name: 'ลูกตุ้มดาวตก',
    slot: 'weapon',
    kind: 'chain',
    rarity: 'rare',
    desc: 'ลูกตุ้มเหล็กมีหนามผูกโซ่ยาว เหวี่ยงเป็นวงกว้าง ศัตรูไม่รู้ว่ามันจะลงมาจากทางไหน',
    stats: { atk: 10, maxHp: 15 },
    visual: 'steel',
    worth: 480,
  },
  {
    id: 'orb-sceptre',
    name: 'คทาลูกแก้วคราม',
    slot: 'weapon',
    kind: 'sceptre',
    rarity: 'rare',
    desc: 'คทาด้ามไม้ลงรัก หัวเป็นลูกแก้วครามในกรงทอง ลูกแก้วสว่างขึ้นทุกครั้งที่เดินลมปราณ',
    stats: { atk: 8, maxHp: 25 },
    visual: 'azure',
    worth: 460,
  },
  {
    id: 'flame-sceptre',
    name: 'คทาเปลวเพลิง',
    slot: 'weapon',
    kind: 'sceptre',
    rarity: 'epic',
    desc: 'คทาทองหัวเป็นเปลวไฟที่ไม่เคยดับ ร้อนจนอากาศรอบตัวสั่นไหว ผู้ถือต้องมีลมปราณกล้าพอจะไม่ถูกเผาเสียเอง',
    stats: { atk: 13, maxHp: 40, crit: 0.02 },
    visual: 'crimson',
    worth: 640,
  },
  {
    id: 'bronze-shield',
    name: 'โล่กลมทองเหลือง',
    slot: 'weapon',
    kind: 'shield',
    rarity: 'common',
    desc: 'โล่ไม้กลมหุ้มขอบทองเหลือง กลางโล่ตอกหมุดเป็นวง รับคมดาบได้ดีกว่าแขนเปล่า',
    stats: { atk: 2, def: 4, maxHp: 10 },
    visual: 'steel',
    worth: 90,
  },
  {
    id: 'beast-shield',
    name: 'โล่หน้าอสูร',
    slot: 'weapon',
    kind: 'shield',
    rarity: 'rare',
    desc: 'โล่เหล็กหล่อเป็นหน้าอสูรแยกเขี้ยว ศัตรูที่ฟันลงบนโล่มักชะงักเพราะสบตามัน',
    stats: { atk: 4, def: 8, maxHp: 30 },
    visual: 'steel',
    worth: 480,
  },
  ...MORE_GEAR,
  // แหวน and หยกห้อย (items v2 accessory slots).
  ...ACCESSORIES,
  // The world bosses' sets, chest pieces and the uniques (data/bossGear.ts).
  ...BOSS_GEAR,
  // ถ้ำคลื่นคำราม's chest: the ชุดคลื่นคำราม set, the pearl and the manual (data/dungeon.ts).
  ...DUNGEON_GEAR,
  // The red sword tassel of the plains opening's M7 (data/plainsFill.ts).
  TASSEL_ITEM,
  // L7: one signature drop per monster family (data/codex.ts).
  ...SIGNATURE_ITEMS,
];

export const ITEMS: Readonly<Record<string, ItemDef>> = Object.fromEntries(list.map((i) => [i.id, i]));
export const ITEM_LIST: readonly ItemDef[] = list;

export function getItem(id: string): ItemDef {
  const i = ITEMS[id];
  if (!i) throw new Error(`Unknown item: ${id}`);
  return i;
}

/** Most items a bag holds. */
export const BAG_SIZE = 24;

/** An item by id: a fixed item, or a rolled one (its base with its own stats). */
export function itemOf(rolls: Readonly<Record<string, ItemRoll>>, id: string): ItemDef | undefined {
  const r = rolls[id];
  if (!r) return ITEMS[id];
  const base = ITEMS[r.base];
  if (!base) return undefined;
  const rs = r.affixes ? rollStats(r) : undefined;
  const art = rs ? firstArt(rs.arts) : r.art;
  return {
    ...base,
    id,
    stats: rs ? rs.stats : r.stats,
    ...(art ? { art } : {}),
    ...(r.forge ? { forge: r.forge } : {}),
    ...(r.rarity ? { rarity: r.rarity } : {}),
    ...(r.ilv ? { ilv: r.ilv } : {}),
    ...(r.affixes ? { affixes: r.affixes } : {}),
  };
}

const firstArt = (arts: Record<string, number>): { id: string; pct: number } | undefined => {
  const k = Object.keys(arts)[0];
  return k ? { id: k, pct: arts[k]! } : undefined;
};

/** An item's level: its own, or by rarity. */
export const baseIlv = (it: Pick<ItemDef, 'ilv' | 'rarity'>): number =>
  it.ilv ?? { common: 5, fine: 10, rare: 15, epic: 25, legend: 35, mythic: 45 }[it.rarity];

/**
 * A rolled item's stats from its base and its affixes (v2), or from its stored `stats`/`art` (pre-v2): the stats, the
 * damage bonus per art and per school.
 */
export function rollStats(r: ItemRoll): {
  stats: ItemStats;
  arts: Record<string, number>;
  schools: Record<string, number>;
} {
  if (!r.affixes) return { stats: r.stats, arts: r.art ? { [r.art.id]: r.art.pct } : {}, schools: {} };
  const base = ITEMS[r.base];
  const stats: ItemStats = { ...(base?.stats ?? {}) };
  const arts: Record<string, number> = {};
  const schools: Record<string, number> = {};
  for (const a of r.affixes) {
    const d = AFFIXES[a.id];
    if (!d) continue;
    if (d.id === 'artDmg' && a.arg) arts[a.arg] = (arts[a.arg] ?? 0) + a.v;
    else if (d.id === 'schoolDmg' && a.arg) schools[a.arg] = (schools[a.arg] ?? 0) + a.v;
    else if (d.stat) stats[d.stat] = round6((stats[d.stat] ?? 0) + a.v);
  }
  for (const gem of r.sockets ?? [])
    if (gem)
      for (const [k, v] of Object.entries(gemStats(gem)) as [keyof ItemStats, number][])
        stats[k] = round6((stats[k] ?? 0) + v);
  return { stats, arts, schools };
}

const round6 = (x: number): number => Math.round(x * 1e6) / 1e6;

/** An item's name with its +, as players read it (กระบี่เหล็กกล้า +7). */
export const itemName = (it: ItemDef): string => (it.forge?.plus ? `${it.name} +${it.forge.plus}` : it.name);

/** An item's stats with what forging adds. */
export function fullStats(it: ItemDef): ItemStats {
  if (!it.forge) return it.stats;
  const s: ItemStats = { ...it.stats };
  // Weapons: + is damage; armour and accessories (items v2): defence and life, elements as on a weapon.
  const f: ItemStats =
    it.slot === 'weapon' ? forgeStats(it.forge, it.stats.atk ?? 0) : forgeStats({ ...it.forge, plus: 0 }, 0);
  if (it.slot !== 'weapon')
    for (const [k, v] of Object.entries(armourPlusStats(it.stats, it.forge.plus)) as [keyof ItemStats, number][])
      f[k] = (f[k] ?? 0) + v;
  for (const k of Object.keys(f) as (keyof ItemStats)[]) {
    const dp = k === 'atk' ? 10 : 1000;
    s[k] = Math.round(((s[k] ?? 0) + f[k]!) * dp) / dp;
  }
  return s;
}

/** What everything equipped adds: every stat (capped, `AFFIX_CAPS`), and the damage bonuses per art and school. */
export type EquipmentStats = Required<ItemStats> & {
  arts: Record<string, number>;
  schools: Record<string, number>;
  /** Pieces worn per set (data/itemSets.ts), and the unique effects worn (one each, data/uniques.ts). */
  sets: Record<string, number>;
  uniques: string[];
};

/**
 * Replaces how one equipped piece counts (data/pvpTemplate.ts: ranked arena and guild war level base stats and +forge).
 * Gets the piece and its roll, returns the stats it adds in place of `fullStats(it)`.
 */
export type GearNormalizer = (it: ItemDef, roll: ItemRoll | undefined) => ItemStats;

/** Sum of the stats of everything equipped, and the martial-art (and school) damage bonuses. */
export function equipmentStats(
  equipment: Partial<Record<EquipSlot, string>>,
  rolls: Readonly<Record<string, ItemRoll>> = {},
  normalize?: GearNormalizer,
): EquipmentStats {
  const s: EquipmentStats = { ...ZERO_STATS, arts: {}, schools: {}, sets: {}, uniques: [] };
  const setPieces: Record<string, Set<string>> = {};
  for (const id of Object.values(equipment)) {
    const it = id ? itemOf(rolls, id) : undefined;
    if (!it) continue;
    const baseId = rolls[id!]?.base ?? id!;
    const set = it.set ?? SET_OF_PIECE[baseId];
    if (set) (setPieces[set] ??= new Set()).add(baseId);
    if (it.unique && !s.uniques.includes(it.unique)) s.uniques.push(it.unique);
    const st = normalize ? normalize(it, rolls[id!]) : fullStats(it);
    for (const k of Object.keys(st) as (keyof ItemStats)[]) if (k in ZERO_STATS) s[k] += st[k] ?? 0;
    const roll = rolls[id!];
    if (roll?.affixes) {
      const rs = rollStats(roll);
      for (const [a, v] of Object.entries(rs.arts)) s.arts[a] = (s.arts[a] ?? 0) + v;
      for (const [a, v] of Object.entries(rs.schools)) s.schools[a] = (s.schools[a] ?? 0) + v;
    } else if (it.art) s.arts[it.art.id] = (s.arts[it.art.id] ?? 0) + it.art.pct;
  }
  // Set bonuses: distinct pieces of one set worn.
  for (const [set, pieces] of Object.entries(setPieces)) {
    s.sets[set] = pieces.size;
    const def = ITEM_SETS[set];
    if (def)
      for (const b of setBonusesAt(def, pieces.size))
        for (const [k, v] of Object.entries(b.stats ?? {}) as [keyof ItemStats, number][]) s[k] += v;
  }
  for (const [k, cap] of Object.entries(AFFIX_CAPS) as [keyof ItemStats, number][]) s[k] = Math.min(cap, s[k]);
  return s;
}

/**
 * Rolls one piece of a base at an item level and rarity (docs/systems/ITEMS_V2.md §5.1), through `rng` only. The base's own
 * art bonus (a manual) and `opts.implicit` ride along outside the budget; then `rollAffixes` draws the rest.
 */
export function rollItem(base: string, ilv: number, rarity: Rarity, rng: Rng, opts: RollOpts = {}): ItemRoll {
  const it = getItem(base);
  const implicit: AffixRoll[] = [
    ...(opts.implicit ?? []),
    ...(it.art ? [{ id: 'artDmg', v: it.art.pct, q: 100, arg: it.art.id, imp: true }] : []),
  ];
  const lv = clampIlv(ilv);
  const affixes = [...implicit, ...rollAffixes(it, lv, rarity, rng, { ...opts, implicit })];
  // Sockets: a fixed count by rarity; a หายาก has a chance of one (one draw, หายาก only).
  const n = rarity === 'rare' ? (rng() < RARE_SOCKET_CHANCE ? 1 : 0) : START_SOCKETS[rarity];
  const sockets = n ? { sockets: Array.from({ length: n }, () => null) } : {};
  return withDerived({ base, ilv: lv, rarity, affixes, stats: {}, rerolls: 0, ...sockets });
}

/**
 * A roll built from chosen affixes instead of drawn ones (the arena and guild shops: fixed pools, no `Rng`). Sockets
 * start as the rarity gives them.
 */
export function composeRoll(base: string, ilv: number, rarity: Rarity, affixes: readonly AffixRoll[]): ItemRoll {
  const n = START_SOCKETS[rarity];
  const sockets = n ? { sockets: Array.from({ length: n }, () => null) } : {};
  return withDerived({ base, ilv: clampIlv(ilv), rarity, affixes: [...affixes], stats: {}, rerolls: 0, ...sockets });
}

/** A roll with its `stats` and `art` filled from its affixes. */
function withDerived(r: ItemRoll): ItemRoll {
  const rs = rollStats(r);
  const { art: _drop, ...rest } = r;
  void _drop;
  const art = firstArt(rs.arts);
  return { ...rest, stats: rs.stats, ...(art ? { art } : {}) };
}

/**
 * A saved roll as a v2 roll (docs/systems/ITEMS_V2.md §5.3). A pre-v2 roll (`stats` + `art`, no `affixes`) becomes affixes:
 * each stat that differs from the base's becomes its affix at quality 100 % with the same value, and the art bonus an
 * `artDmg` signature, so `equipmentStats` is unchanged. A v2 roll keeps its known affixes with finite values.
 * Returns undefined for a roll that cannot load (unknown base).
 */
export function migrateRoll(raw: ItemRoll): ItemRoll | undefined {
  const base = ITEMS[raw.base];
  if (!base) return undefined;
  const rarity = isRarity(raw.rarity) ? raw.rarity : base.rarity;
  const ilv = clampIlv(typeof raw.ilv === 'number' ? raw.ilv : baseIlv(base));
  const sockets = Array.isArray(raw.sockets)
    ? raw.sockets.slice(0, MAX_SOCKETS[rarity]).map((g) => (typeof g === 'string' && gemOf(g) ? g : null))
    : [];
  const keep = {
    ...(raw.forge ? { forge: raw.forge } : {}),
    ...(sockets.length ? { sockets } : {}),
    rerolls: typeof raw.rerolls === 'number' && raw.rerolls >= 0 ? Math.floor(raw.rerolls) : 0,
  };
  if (Array.isArray(raw.affixes)) {
    const affixes = raw.affixes
      .filter((a) => a && typeof a === 'object' && AFFIXES[a.id] && Number.isFinite(a.v))
      .map((a) => ({
        id: a.id,
        v: a.v,
        q: Number.isFinite(a.q) ? Math.max(0, Math.min(100, Math.round(a.q))) : 100,
        ...(typeof a.arg === 'string' && a.arg ? { arg: a.arg } : {}),
        ...(a.locked ? { locked: true } : {}),
        ...(a.imp ? { imp: true } : {}),
      }));
    return withDerived({ base: raw.base, ilv, rarity, affixes, stats: {}, ...keep });
  }
  const affixes: AffixRoll[] = [];
  const art = raw.art ?? base.art;
  if (art && typeof art.id === 'string' && Number.isFinite(art.pct))
    affixes.push({ id: 'artDmg', v: art.pct, q: 100, arg: art.id, imp: true });
  const stats = (raw.stats ?? {}) as ItemStats;
  for (const k of Object.keys(stats) as (keyof ItemStats)[]) {
    const v = stats[k];
    const id = AFFIX_OF_STAT[k];
    if (!id || typeof v !== 'number' || !Number.isFinite(v)) continue;
    const d = round6(v - (base.stats[k] ?? 0));
    if (d !== 0) affixes.push({ id, v: d, q: 100 });
  }
  // Stats the base has and the old roll dropped stay the base's (old rolls only ever added or overwrote).
  return withDerived({ base: raw.base, ilv, rarity, affixes, stats: {}, ...keep });
}


/**
 * Rolls a legendary weapon (the world boss's prize): +30 % damage to one of the given arts (picked at random) as its
 * signature, then the random affixes of its rarity at its item level (`rollItem`).
 */
export function rollWeapon(base: string, arts: readonly string[], rng: Rng): ItemRoll {
  const it = getItem(base);
  const art = arts[Math.floor(rng() * arts.length)]!;
  return rollItem(base, baseIlv(it), it.rarity, rng, {
    implicit: [{ id: 'artDmg', v: 0.3, q: 100, arg: art, imp: true }],
  });
}
